import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import SecureCookieManager from '../utils/secureCookie';
import { getV2Token } from '../utils/v2AuthUtils';
import { getFederation } from '../services/federationServices';
import type { Federation, GuestRole } from '../types/federation';

/**
 * Active federation context — tracks whether the caller is currently acting
 * as a Guest inside another tenant's workspace.
 *
 * The federation_id is encoded in the JWT (claim added when the federated
 * party logs in via the federation key). When set, the platform shows a
 * "Guest Architect inside X" banner and gates reads/writes to the
 * federation's scope.
 *
 * Backend ask: include `federation_id` and `guest_role` claims on JWTs
 * issued under a federation key. See docs/federation-backend.md §4.
 */

interface FederationJwtClaims {
  federation_id?: string;
  guest_role?: GuestRole;
}

interface ActiveFederationContextValue {
  /** Active federation when the caller is acting as a Guest. Null otherwise. */
  active: Federation | null;
  /** Guest role granted by this federation. Null when not acting as Guest. */
  guestRole: GuestRole | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const ActiveFederationContext = createContext<ActiveFederationContextValue | undefined>(
  undefined,
);

function readToken(): string | null {
  return SecureCookieManager.getAuthToken() || getV2Token() || null;
}

function decodeFederationClaims(): FederationJwtClaims {
  const token = readToken();
  if (!token) return {};
  try {
    return jwtDecode<FederationJwtClaims>(token);
  } catch {
    return {};
  }
}

export const ActiveFederationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [active, setActive] = useState<Federation | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const claims = useMemo(() => decodeFederationClaims(), []);
  const guestRole = claims.guest_role ?? null;

  const fetchActive = useCallback(async () => {
    if (!claims.federation_id) {
      setActive(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const fed = await getFederation(claims.federation_id);
      setActive(fed);
    } catch {
      setActive(null);
    } finally {
      setIsLoading(false);
    }
  }, [claims.federation_id]);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  const value: ActiveFederationContextValue = {
    active,
    guestRole,
    isLoading,
    refresh: fetchActive,
  };

  return (
    <ActiveFederationContext.Provider value={value}>
      {children}
    </ActiveFederationContext.Provider>
  );
};

export function useActiveFederation(): ActiveFederationContextValue {
  const ctx = useContext(ActiveFederationContext);
  if (!ctx) {
    // Provider is mounted near the root; if a component is rendered outside
    // it (e.g. in tests), return a safe default so we don't crash.
    return { active: null, guestRole: null, isLoading: false, refresh: async () => {} };
  }
  return ctx;
}
